import mixpanel from "mixpanel-browser";
import { BaseAnalyticsService } from "@/modules/analytics/services/base-analytics.service";
import { DecodedToken } from "@/modules/common/context/auth/auth.context";
import { EnvironmentVariables } from "@/modules/common/types";

export class MixpanelAnalyticsService extends BaseAnalyticsService {
  private initialized = false;
  private readonly token: string;
  private readonly config: Record<string, unknown>;

  constructor() {
    super();
    this.token =
      EnvironmentVariables.MIXPANEL_TOKEN?.toString() || "MIXPANEL_TOKEN";

    this.config = {
      track_pageview: false,
      persistence: "localStorage",
      ignore_dnt: false,
      secure_cookie: true,
      batch_requests: true,
    };
  }

  async init() {
    try {
      if (this.initialized) {
        console.warn("Mixpanel already initialized");
        return;
      }

      mixpanel.init(this.token, this.config);
      this.initialized = true;

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("✅ Mixpanel initialized successfully");
      }
    } catch (error) {
      console.error("❌ Mixpanel initialization failed:", error);
      throw error;
    }
  }

  identify(user: DecodedToken) {
    if (!this.initialized) {
      console.warn("⚠️ Mixpanel not initialized, skipping identify");
      return;
    }

    try {
      const userId = user["cognito:username"] || user.sub;
      mixpanel.identify(userId);

      const userProps: Record<string, unknown> = {
        $email: user.email || undefined,
        userId: userId,
        uid: userId,
        phone: user.phone_number || undefined,
        groups: user["cognito:groups"] || [],
      };

      // Only set stable, non-mutable properties. Avoid emitting a separate "User Identified" event
      // or adding timestamps that change on each identify to keep identify calls idempotent.
      mixpanel.people.set(userProps);

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("👤 Mixpanel user identified (stable props):", userProps);
      }
    } catch (error) {
      console.error("❌ Mixpanel identify failed:", error);
    }
  }

  setUserAttributes(attributes: Record<string, unknown>) {
    if (!this.initialized) {
      console.warn("Mixpanel not initialized, skipping setUserAttributes");
      return;
    }

    try {
      const cleanedAttributes = Object.fromEntries(
        Object.entries(attributes).filter(
          ([, value]) => value !== undefined && value !== null
        )
      );

      mixpanel.people.set(cleanedAttributes);

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("📝 Mixpanel user attributes updated:", cleanedAttributes);
      }
    } catch (error) {
      console.error("❌ Mixpanel setUserAttributes failed:", error);
    }
  }

  pageView(event: string, customAttributes?: Record<string, unknown>) {
    if (!this.initialized) {
      console.warn("Mixpanel not initialized, skipping pageView");
      return;
    }

    try {
      const payload = {
        ...customAttributes,
        page_title: document.title,
        timestamp: new Date().toISOString(),
      };

      mixpanel.track(event, payload);

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("📄 Mixpanel page view:", event, payload);
      }
    } catch (error) {
      console.error("❌ Mixpanel pageView failed:", error);
    }
  }

  track(event: string, payload: Record<string, unknown> = {}) {
    if (!this.initialized) {
      console.warn("Mixpanel not initialized, skipping track");
      return;
    }

    try {
      const enrichedPayload = {
        ...payload,
        event_timestamp: new Date().toISOString(),
      };

      mixpanel.track(event, enrichedPayload);

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("📊 Mixpanel event tracked:", event, enrichedPayload);
      }
    } catch (error) {
      console.error("❌ Mixpanel track failed:", error);
    }
  }

  unidentify() {
    if (!this.initialized) {
      console.warn("Mixpanel not initialized, skipping unidentify");
      return;
    }

    try {
      mixpanel.reset();

      if (EnvironmentVariables.Environment?.toString() !== "production") {
        console.log("🔓 Mixpanel user session cleared");
      }
    } catch (error) {
      console.error("❌ Mixpanel unidentify failed:", error);
    }
  }
}
